import { IComponentOptions, IController, IOnChangesObject } from "angular";
import { DynamicFilterType, dynamicFilterTypeSet } from "@gtmhub/shared/components/generic-session-selector/dynamic-filters-presets";

export enum ConditionDynamicFilterType {
  activeSessions = "current",
  pastSessions = "past",
  upcomingSessions = "future",
  openSessions = "open",
  inProgressSessions = "locked",
  notSetSessions = "empty",
}

const dynamicFilterTypeValues: Record<DynamicFilterType, ConditionDynamicFilterType> = {
  [DynamicFilterType.activeSessions]: ConditionDynamicFilterType.activeSessions,
  [DynamicFilterType.pastSessions]: ConditionDynamicFilterType.pastSessions,
  [DynamicFilterType.upcomingSessions]: ConditionDynamicFilterType.upcomingSessions,
  [DynamicFilterType.openSessions]: ConditionDynamicFilterType.openSessions,
  [DynamicFilterType.inProgressSessions]: ConditionDynamicFilterType.inProgressSessions,
  [DynamicFilterType.notSetSessions]: ConditionDynamicFilterType.notSetSessions,
};

export const automationSessionDynamicValueTypes: Record<ConditionDynamicFilterType, DynamicFilterType> = {
  [ConditionDynamicFilterType.activeSessions]: DynamicFilterType.activeSessions,
  [ConditionDynamicFilterType.pastSessions]: DynamicFilterType.pastSessions,
  [ConditionDynamicFilterType.upcomingSessions]: DynamicFilterType.upcomingSessions,
  [ConditionDynamicFilterType.openSessions]: DynamicFilterType.openSessions,
  [ConditionDynamicFilterType.inProgressSessions]: DynamicFilterType.inProgressSessions,
  [ConditionDynamicFilterType.notSetSessions]: DynamicFilterType.notSetSessions,
};

export interface IAutomationSessionSelectorBindings {
  value: string;
  onChange?({ value: string }): void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface AutomationSessionSelectorCtrl extends IAutomationSessionSelectorBindings {}

/**
 * Provides functionality for selecting sessions form a drop down list
 * when creating conditions for automation tasks.
 *
//  * `value` a string containing a comma separated list of selected session ids and dynamic session filters.
//  * `on-change` an event handler accepting the new value - a string containing a comma separated list.
 *
 * @example
 * <automation-session-selector
//  *   value="$ctrl.value"
//  *   on-change="$ctrl.valueChanged(value)"
 * </automation-session-selector>
 */
export class AutomationSessionSelectorCtrl implements IController {
  // bindings
  value = ""; // contains dynamic filters in automation task session filter types, e.g. "current"
  // onChange?: ({ value: string }) => void;

  mappedValue: string;

  $onChanges(onChangesObj: IOnChangesObject): void {
    if (onChangesObj && onChangesObj.value && onChangesObj.value.currentValue !== onChangesObj.value.previousValue) {
      // could be UNINITIALIZED_VALUE {}
      if (typeof this.value === "string") {
        const idsAndDynamicFilters = this.value.split(",");
        this.mappedValue = idsAndDynamicFilters
          .map((idOrDynamicFilter) => {
            if (idOrDynamicFilter in automationSessionDynamicValueTypes) {
              return automationSessionDynamicValueTypes[idOrDynamicFilter];
            } else {
              return idOrDynamicFilter;
            }
          })
          .join(",");
      } else {
        this.mappedValue = "";
      }
    }
  }

  onChanged(value: string) {
    const idsAndDynamicFilters = value.split(",");
    const newValue = idsAndDynamicFilters
      .map((idOrDynamicFilter) => {
        if (dynamicFilterTypeSet.has(idOrDynamicFilter)) {
          return dynamicFilterTypeValues[idOrDynamicFilter];
        } else {
          return idOrDynamicFilter;
        }
      })
      .join(",");
    this.notifyOnChange(newValue);
  }

  private notifyOnChange(value: string) {
    if (this.onChange) {
      this.onChange({ value });
    }
  }
}

export const AutomationSessionSelectorComponent: IComponentOptions = {
  controller: AutomationSessionSelectorCtrl,
  templateUrl: require("./automation-session-selector.html"),
  bindings: {
    value: "<",
    onChange: "&",
  },
};
